import React, { useState, useEffect } from "react";
import Calendar from "react-calendar";
import {
    FaBan, FaCheck, FaClipboardList, FaClock, FaEdit, FaEllipsisV,
    FaFileDownload, FaNotesMedical, FaRedoAlt, FaTrashAlt, FaUserMd
} from "react-icons/fa";
import TreatmentsEpisodes from "./TreatmentsEpisodes";
import { 
    getAllDetailTreatments,
    updateDetailTreatment,
    deleteDetailTreatment,
    updateTreatmentProgress,
    getDetailTreatmentById
} from "../../components/PatientTreatmentDetails/services/detailTreatmentService";

// Componente Modal de Confirmación
const ConfirmationModal = ({ isOpen, onClose, onConfirm, title, message, confirmText = "Confirmar", cancelText = "Cancelar", isDanger = false }) => {
    if (!isOpen) return null;

    return (
        <div className="modal show d-block" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }} onClick={onClose}>
            <div className="modal-dialog modal-dialog-centered" onClick={(e) => e.stopPropagation()}>
                <div className="modal-content">
                    <div className="modal-header">
                        <h5 className="modal-title">{title}</h5>
                        <button type="button" className="btn-close" onClick={onClose}></button>
                    </div>
                    <div className="modal-body">
                        <p className="mb-0">{message}</p>
                    </div>
                    <div className="modal-footer">
                        <button type="button" className="btn btn-secondary" onClick={onClose}>
                            {cancelText}
                        </button>
                        <button 
                            type="button" 
                            className={`btn ${isDanger ? 'btn-danger' : 'btn-primary'}`}
                            onClick={() => {
                                onConfirm();
                                onClose();
                            }}
                        >
                            {confirmText}
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
};

const CustomCalendar = () => {
    const [date, setDate] = useState(new Date());
    const [selectedEvents, setSelectedEvents] = useState([]);
    const [activeActionsMenuIndex, setActiveActionsMenuIndex] = useState(null);
    const [showEpisodesPanel, setShowEpisodesPanel] = useState(false);
    const [events, setEvents] = useState({});
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [selectedTreatmentId, setSelectedTreatmentId] = useState(null);
    
    // Estado para el modal de confirmación
    const [confirmModal, setConfirmModal] = useState({
        isOpen: false,
        title: '',
        message: '',
        onConfirm: () => {},
        isDanger: false
    });

    useEffect(() => {
        fetchTreatments();
    }, []);

    useEffect(() => {
        const handleClickOutside = (event) => {
            if (activeActionsMenuIndex !== null && !event.target.closest('.actions-menu')) {
                setActiveActionsMenuIndex(null);
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, [activeActionsMenuIndex]);

    const showConfirmation = (title, message, onConfirm, isDanger = false) => {
        setConfirmModal({
            isOpen: true,
            title,
            message,
            onConfirm,
            isDanger
        });
    };

    const closeConfirmation = () => {
        setConfirmModal({
            isOpen: false,
            title: '',
            message: '',
            onConfirm: () => {},
            isDanger: false
        });
    };

    const fetchTreatments = async () => {
        try {
            setLoading(true);
            setError(null);
            
            const response = await getAllDetailTreatments({ limit: 1000 });
            
            if (response && response.data) {
                const processedEvents = processTreatmentsToEvents(response.data);
                setEvents(processedEvents);
                
                const formattedDate = new Date().toISOString().split("T")[0];
                setSelectedEvents(processedEvents[formattedDate] || []);
            }
        } catch (err) {
            console.error("Error fetching treatments:", err);
            setError("No se pudieron cargar los tratamientos");
        } finally {
            setLoading(false);
        }
    };

    const processTreatmentsToEvents = (treatments) => {
        const eventsMap = {};
        
        treatments.forEach((treatment) => {
            const startDate = new Date(treatment.treatment_info.start_date);
            const endDate = treatment.treatment_info.estimated_end_date 
                ? new Date(treatment.treatment_info.estimated_end_date)
                : null;

            const eventColor = getStatusColor(treatment.treatment_info.status);
            
            const startDateKey = startDate.toISOString().split("T")[0];
            const event = {
                title: treatment.treatment_info.name,
                time: startDate.toLocaleTimeString('es-ES', { hour: '2-digit', minute: '2-digit' }),
                doctor: treatment.doctor_info?.doctor_name || "Sin asignar",
                color: eventColor,
                status: treatment.treatment_info.status,
                treatmentId: treatment._id,
                type: 'start',
                protocol: treatment.treatment_info.protocol_code,
                patientId: treatment.patient_id,
                fullTreatment: treatment
            };

            if (!eventsMap[startDateKey]) {
                eventsMap[startDateKey] = [];
            }
            eventsMap[startDateKey].push(event);

            if (endDate) {
                const endDateKey = endDate.toISOString().split("T")[0];
                const endEvent = {
                    ...event,
                    title: `${treatment.treatment_info.name} (Fin)`,
                    time: endDate.toLocaleTimeString('es-ES', { hour: '2-digit', minute: '2-digit' }),
                    type: 'end'
                };

                if (!eventsMap[endDateKey]) {
                    eventsMap[endDateKey] = [];
                }
                eventsMap[endDateKey].push(endEvent);
            }
        });

        return eventsMap;
    };

    const getStatusColor = (status) => {
        const statusColors = {
            'pending': '#FFA500',
            'active': '#28A745',
            'completed': '#6C757D',
            'suspended': '#DC3545',
            'canceled': '#6C757D'
        };
        return statusColors[status] || '#6C757D';
    };

    const onChange = (selectedDate) => {
        setDate(selectedDate);
        const formattedDate = selectedDate.toISOString().split("T")[0];
        setSelectedEvents(events[formattedDate] || []);
        setActiveActionsMenuIndex(null);
    };

    const tileContent = ({ date }) => {
        const formattedDate = date.toISOString().split("T")[0];
        if (events[formattedDate] && events[formattedDate].length > 0) {
            return (
                <div className="d-flex flex-column align-items-center mt-1 gap-1">
                    {events[formattedDate].slice(0, 2).map((event, index) => (
                        <span
                            key={index}
                            className="badge rounded-pill"
                            style={{ 
                                backgroundColor: event.color, 
                                color: "#fff",
                                fontSize: "9px",
                                whiteSpace: "nowrap",
                                padding: "2px 6px"
                            }}
                        >
                            {event.title.length > 15 ? `${event.title.substring(0, 15)}...` : event.title}
                        </span>
                    ))}
                    {events[formattedDate].length > 2 && (
                        <span className="badge bg-secondary rounded-pill" style={{ fontSize: "9px", padding: "2px 6px" }}>
                            +{events[formattedDate].length - 2}
                        </span>
                    )}
                </div>
            );
        }
        return null;
    };

    const tileClassName = ({ date }) => {
        const formattedDate = date.toISOString().split("T")[0];
        const today = new Date();
        const todayFormatted = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}-${String(today.getDate()).padStart(2, '0')}`;
        
        if (formattedDate === todayFormatted) {
            return "border border-primary border-2 rounded";
        }
        
        return events[formattedDate] && events[formattedDate].length > 0 
            ? "bg-light rounded" 
            : "";
    };

    const formatDate = (date) => {
        return date.toLocaleDateString("es-ES", { 
            weekday: 'long',
            day: "2-digit", 
            month: "long", 
            year: "numeric" 
        });
    };

    const toggleActionsMenu = (index) => {
        setActiveActionsMenuIndex(activeActionsMenuIndex === index ? null : index);
    };

    const openEpisodesPanel = (treatmentId) => {
        setSelectedTreatmentId(treatmentId);
        setShowEpisodesPanel(true);
        setActiveActionsMenuIndex(null);
    };

    const closeEpisodesPanel = () => {
        setShowEpisodesPanel(false);
        setSelectedTreatmentId(null);
    };

    const getStatusBadge = (status) => {
        const statusLabels = {
            'pending': 'Pendiente',
            'active': 'Activo',
            'completed': 'Completado',
            'suspended': 'Suspendido',
            'canceled': 'Cancelado'
        };
        
        const statusClasses = {
            'pending': 'bg-warning',
            'active': 'bg-success',
            'completed': 'bg-secondary',
            'suspended': 'bg-danger',
            'canceled': 'bg-dark'
        };
        
        return (
            <span className={`badge ${statusClasses[status] || 'bg-secondary'} ms-2`}>
                {statusLabels[status] || status}
            </span>
        );
    };

    const handleViewDetails = async (event) => {
        try {
            setActiveActionsMenuIndex(null);
            const treatment = await getDetailTreatmentById(event.treatmentId);
            console.log('Treatment details:', treatment);
            alert(`Detalles del tratamiento:\n${JSON.stringify(treatment, null, 2)}`);
        } catch (error) {
            console.error('Error viewing details:', error);
            alert('Error al cargar los detalles del tratamiento');
        }
    };

    const handleEdit = (event) => {
        setActiveActionsMenuIndex(null);
        console.log('Edit treatment:', event.treatmentId);
        alert(`Editar tratamiento: ${event.title}\nID: ${event.treatmentId}`);
    };

    const handleRegisterProgress = async (event) => {
        try {
            setActiveActionsMenuIndex(null);
            const newProgress = prompt('Ingrese el porcentaje de progreso (0-100):', '0');
            
            if (newProgress !== null) {
                const progressValue = parseInt(newProgress);
                if (progressValue >= 0 && progressValue <= 100) {
                    await updateTreatmentProgress(event.treatmentId, {
                        progress_percentage: progressValue,
                        last_update: new Date().toISOString()
                    });
                    alert('Progreso actualizado exitosamente');
                    await fetchTreatments();
                } else {
                    alert('El progreso debe estar entre 0 y 100');
                }
            }
        } catch (error) {
            console.error('Error updating progress:', error);
            alert('Error al actualizar el progreso');
        }
    };

    const handleAddSession = (event) => {
        setActiveActionsMenuIndex(null);
        console.log('Add session to treatment:', event.treatmentId);
        alert(`Añadir sesión al tratamiento: ${event.title}`);
    };

    const handleReschedule = (event) => {
        setActiveActionsMenuIndex(null);
        const newDate = prompt('Ingrese la nueva fecha (YYYY-MM-DD):');
        if (newDate) {
            console.log('Reschedule treatment:', event.treatmentId, 'to', newDate);
            alert(`Reprogramar tratamiento a: ${newDate}`);
        }
    };

    const handleMarkCompleted = async (event) => {
        setActiveActionsMenuIndex(null);
        showConfirmation(
            'Marcar como completado',
            `¿Marcar como completado el tratamiento "${event.title}"?`,
            async () => {
                try {
                    await updateDetailTreatment(event.treatmentId, {
                        treatment_info: {
                            ...event.fullTreatment.treatment_info,
                            status: 'completed',
                            progress_percentage: 100,
                            last_update: new Date().toISOString()
                        }
                    });
                    alert('Tratamiento marcado como completado');
                    await fetchTreatments();
                } catch (error) {
                    console.error('Error marking as completed:', error);
                    alert('Error al marcar como completado');
                }
            }
        );
    };

    const handleMarkCanceled = async (event) => {
        setActiveActionsMenuIndex(null);
        showConfirmation(
            'Cancelar tratamiento',
            `¿Marcar como cancelado el tratamiento "${event.title}"?`,
            async () => {
                try {
                    await updateDetailTreatment(event.treatmentId, {
                        treatment_info: {
                            ...event.fullTreatment.treatment_info,
                            status: 'canceled',
                            last_update: new Date().toISOString()
                        }
                    });
                    alert('Tratamiento cancelado');
                    await fetchTreatments();
                } catch (error) {
                    console.error('Error canceling treatment:', error);
                    alert('Error al cancelar el tratamiento');
                }
            },
            true
        );
    };

    const handleReactivate = async (event) => {
        setActiveActionsMenuIndex(null);
        showConfirmation(
            'Reactivar tratamiento',
            `¿Reactivar el tratamiento "${event.title}"?`,
            async () => {
                try {
                    await updateDetailTreatment(event.treatmentId, {
                        treatment_info: {
                            ...event.fullTreatment.treatment_info,
                            status: 'active',
                            last_update: new Date().toISOString()
                        }
                    });
                    alert('Tratamiento reactivado');
                    await fetchTreatments();
                } catch (error) {
                    console.error('Error reactivating treatment:', error);
                    alert('Error al reactivar el tratamiento');
                }
            }
        );
    };

    const handleGenerateReport = (event) => {
        setActiveActionsMenuIndex(null);
        console.log('Generate report for treatment:', event.treatmentId);
        alert(`Generar reporte para: ${event.title}\nEsta funcionalidad se implementará próximamente`);
    };

    const handleViewPatientTreatment = (event) => {
        setActiveActionsMenuIndex(null);
        console.log('View patient treatment:', event.patientId);
        alert(`Ver tratamiento del paciente\nPatient ID: ${event.patientId}`);
    };

    const handleViewMedicalData = (event) => {
        setActiveActionsMenuIndex(null);
        console.log('View medical data for patient:', event.patientId);
        alert(`Ver datos médicos y clínicos\nPatient ID: ${event.patientId}`);
    };

    const handleDelete = async (event) => {
        setActiveActionsMenuIndex(null);
        showConfirmation(
            'Eliminar tratamiento',
            `¿Está seguro de eliminar el tratamiento "${event.title}"?\nEsta acción no se puede deshacer.`,
            async () => {
                try {
                    await deleteDetailTreatment(event.treatmentId);
                    alert('Tratamiento eliminado exitosamente');
                    await fetchTreatments();
                } catch (error) {
                    console.error('Error deleting treatment:', error);
                    alert('Error al eliminar el tratamiento');
                }
            },
            true
        );
    };

    if (loading) {
        return (
            <div className="container-fluid mt-4">
                <div className="text-center py-5">
                    <div className="spinner-border text-primary" role="status">
                        <span className="visually-hidden">Cargando...</span>
                    </div>
                    <p className="mt-3 text-muted">Cargando tratamientos...</p>
                </div>
            </div>
        );
    }

    if (error) {
        return (
            <div className="container-fluid mt-4">
                <div className="alert alert-danger" role="alert">
                    <h5 className="alert-heading">Error</h5>
                    <p>{error}</p>
                    <button className="btn btn-primary" onClick={fetchTreatments}>
                        Reintentar
                    </button>
                </div>
            </div>
        );
    }

    return (
        <div className="container-fluid mt-4">
            <div className="row g-3">
                <div className="col-lg-8">
                    <div className="card shadow-sm">
                        <div className="card-header bg-white border-bottom">
                            <h5 className="mb-0 fw-semibold">Calendario de Tratamientos</h5>
                        </div>
                        <div className="card-body p-4">
                            <Calendar 
                                onChange={onChange} 
                                value={date} 
                                tileContent={tileContent} 
                                tileClassName={tileClassName}
                                locale="es-ES"
                                className="w-100 border-0"
                            />
                        </div>
                    </div>
                </div>

                <div className="col-lg-4">
                    <div className="card shadow-sm sticky-top" style={{ top: '20px' }}>
                        <div className="card-header bg-primary text-white">
                            <h5 className="mb-0 fw-semibold text-capitalize">{formatDate(date)}</h5>
                        </div>
                        <div className="card-body" style={{ maxHeight: '600px', overflowY: 'auto' }}>
                            {selectedEvents.length > 0 ? (
                                <ul className="list-unstyled mb-0">
                                    {selectedEvents.map((event, index) => (
                                        <li key={index} className="position-relative border-bottom pb-3 mb-3">
                                            <div className="d-flex align-items-start justify-content-between">
                                                <div className="flex-grow-1">
                                                    <div className="d-flex align-items-center mb-2">
                                                        <span 
                                                            className="badge me-2" 
                                                            style={{ 
                                                                backgroundColor: event.color,
                                                                fontSize: '10px',
                                                                padding: '4px 8px'
                                                            }}
                                                        >
                                                            {event.time}
                                                        </span>
                                                        <span className="badge bg-light text-dark" style={{ fontSize: '10px' }}>
                                                            {event.type === 'start' ? 'Inicio' : 'Fin'}
                                                        </span>
                                                    </div>
                                                    
                                                    <div className="mb-1">
                                                        <strong className="d-block">{event.title}</strong>
                                                        {getStatusBadge(event.status)}
                                                    </div>
                                                    
                                                    <small className="text-muted d-block">
                                                        <FaUserMd className="me-1" />
                                                        {event.doctor}
                                                    </small>
                                                    
                                                    <small className="text-muted d-block">
                                                        <FaClipboardList className="me-1" />
                                                        {event.protocol}
                                                    </small>
                                                </div>
                                                
                                                <button 
                                                    className="btn btn-sm btn-link text-dark p-1 actions-menu" 
                                                    onClick={() => toggleActionsMenu(index)}
                                                >
                                                    <FaEllipsisV />
                                                </button>
                                            </div>

                                            {activeActionsMenuIndex === index && (
                                                <div 
                                                    className="position-absolute card shadow border-0 actions-menu" 
                                                    style={{ 
                                                        right: "10px", 
                                                        top: "35px", 
                                                        width: "220px", 
                                                        zIndex: 10 
                                                    }}
                                                >
                                                    <ul className="list-unstyled mb-0">
                                                        <li>
                                                            <button 
                                                                className="btn btn-sm w-100 text-start d-flex align-items-center gap-2 py-2 px-3 border-0 rounded-0"
                                                                onClick={() => handleViewDetails(event)}
                                                            >
                                                                <FaClipboardList /> Ver Detalles
                                                            </button>
                                                        </li>
                                                        <li>
                                                            <button 
                                                                className="btn btn-sm w-100 text-start d-flex align-items-center gap-2 py-2 px-3 border-0 rounded-0"
                                                                onClick={() => handleEdit(event)}
                                                            >
                                                                <FaEdit /> Editar
                                                            </button>
                                                        </li>
                                                        <li>
                                                            <button 
                                                                className="btn btn-sm w-100 text-start d-flex align-items-center gap-2 py-2 px-3 border-0 rounded-0"
                                                                onClick={() => handleRegisterProgress(event)}
                                                            >
                                                                <FaClock /> Registrar Progreso
                                                            </button>
                                                        </li>
                                                        <li>
                                                            <button 
                                                                className="btn btn-sm w-100 text-start d-flex align-items-center gap-2 py-2 px-3 border-0 rounded-0"
                                                                onClick={() => handleAddSession(event)}
                                                            >
                                                                <FaCheck /> Añadir Sesión
                                                            </button>
                                                        </li>
                                                        <li>
                                                            <button 
                                                                className="btn btn-sm w-100 text-start d-flex align-items-center gap-2 py-2 px-3 border-0 rounded-0"
                                                                onClick={() => handleReschedule(event)}
                                                            >
                                                                <FaClock /> Reprogramar
                                                            </button>
                                                        </li>
                                                        <li>
                                                            <button 
                                                                className="btn btn-sm w-100 text-start d-flex align-items-center gap-2 py-2 px-3 border-0 rounded-0"
                                                                onClick={() => handleMarkCompleted(event)}
                                                            >
                                                                <FaCheck /> Marcar Completado
                                                            </button>
                                                        </li>
                                                        <li>
                                                            <button 
                                                                className="btn btn-sm w-100 text-start d-flex align-items-center gap-2 py-2 px-3 border-0 rounded-0"
                                                                onClick={() => handleMarkCanceled(event)}
                                                            >
                                                                <FaBan /> Marcar Cancelado
                                                            </button>
                                                        </li>
                                                        <li>
                                                            <button 
                                                                className="btn btn-sm w-100 text-start d-flex align-items-center gap-2 py-2 px-3 border-0 rounded-0"
                                                                onClick={() => handleReactivate(event)}
                                                            >
                                                                <FaRedoAlt /> Reactivar
                                                            </button>
                                                        </li>
                                                        <li>
                                                            <button 
                                                                className="btn btn-sm w-100 text-start d-flex align-items-center gap-2 py-2 px-3 border-0 rounded-0"
                                                                onClick={() => handleGenerateReport(event)}
                                                            >
                                                                <FaFileDownload /> Generar Reporte
                                                            </button>
                                                        </li>
                                                        <li>
                                                            <button 
                                                                className="btn btn-sm w-100 text-start d-flex align-items-center gap-2 py-2 px-3 border-0 rounded-0"
                                                                onClick={() => openEpisodesPanel(event.treatmentId)}
                                                            >
                                                                <FaClipboardList /> Episodios
                                                            </button>
                                                        </li>
                                                        <li>
                                                            <button 
                                                                className="btn btn-sm w-100 text-start d-flex align-items-center gap-2 py-2 px-3 border-0 rounded-0"
                                                                onClick={() => handleViewPatientTreatment(event)}
                                                            >
                                                                <FaUserMd /> Tratamiento del Paciente
                                                            </button>
                                                        </li>
                                                        <li>
                                                            <button 
                                                                className="btn btn-sm w-100 text-start d-flex align-items-center gap-2 py-2 px-3 border-0 rounded-0"
                                                                onClick={() => handleViewMedicalData(event)}
                                                            >
                                                                <FaNotesMedical /> Datos Médicos y Clínicos
                                                            </button>
                                                        </li>
                                                        <li>
                                                            <button 
                                                                className="btn btn-sm w-100 text-start d-flex align-items-center gap-2 py-2 px-3 border-0 rounded-0 text-danger"
                                                                onClick={() => handleDelete(event)}
                                                            >
                                                                <FaTrashAlt /> Eliminar
                                                            </button>
                                                        </li>
                                                    </ul>
                                                </div>
                                            )}
                                        </li>
                                    ))}
                                </ul>
                            ) : (
                                <div className="text-center py-5">
                                    <FaClipboardList size={48} className="text-muted mb-3" />
                                    <p className="text-muted mb-0">No hay tratamientos programados para esta fecha</p>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            </div>

            {showEpisodesPanel && (
                <TreatmentsEpisodes 
                    onClose={closeEpisodesPanel}
                    treatmentId={selectedTreatmentId}
                />
            )}

            <ConfirmationModal
                isOpen={confirmModal.isOpen}
                onClose={closeConfirmation}
                onConfirm={confirmModal.onConfirm}
                title={confirmModal.title}
                message={confirmModal.message}
                isDanger={confirmModal.isDanger}
            />
        </div>
    );
};

export default CustomCalendar;